import React, { useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import { useCreate, useNotify, useRefresh, useStore } from "react-admin";
import { CircularProgress } from "@mui/material";
import { MdOutlineUploadFile } from "react-icons/md";
import ImportResultDialog from "./ImportResultDialog";

const file_type = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
];
// const file_size = 4000 * 1024;

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const InputFileImport = ({
  buttonText = "Import CSV",
  importType,
  startIcon=false,
  ...props
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [switchSubscriberObj] = useStore("switchSubscriberName");
  const [importResult, setImportResult] = useState(null);
  const showsImportResult = importType === "Unit" || importType === "Floor";
  const buttonStyle: any = {
    color: "#2DC3E8",
    border: "1px solid #2DC3E8",
    borderRadius: "4px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontFamily: "OpenSans-Bold",
    textTransform: "capitalize",
    margin: "0px 0px 0px 10px",
    textAlign: "center",
    minWidth:"7rem",
  };

  const [create, { isLoading }] = useCreate();

  const refreshAfterClose = useRef(false);

  const showImportOutcome = (result) => {
    setImportResult(result);
    refreshAfterClose.current = shouldRefreshImportList(result);
  };

  const closeImportResult = () => {
    setImportResult(null);
    if (refreshAfterClose.current) {
      refreshAfterClose.current = false;
      refresh();
    }
  };

  // form submit handler
  const onSubmit = (value) => {
    const formData: any = new FormData();
    formData.append("importType", importType);
    formData.append("SubscriberID", switchSubscriberObj?.value);
    formData.append("file", value);
    if (props?.propertyID) {
      formData.append("PropertyID", props?.propertyID);
    }
    setImportResult(null);
    create(
      `import-xls`,
      { data: formData },
      {
        onSuccess: (data) => {
          if (showsImportResult && data?.records?.overallResult) {
            showImportOutcome(data.records);
            return;
          }
          notify(`File import successfully.`, {
            type: "success",
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          refresh();
        },
        onError: (e: any) => {
          if (showsImportResult && e?.body?.records?.overallResult) {
            showImportOutcome(e.body.records);
            return;
          }
          if (showsImportResult) {
            showImportOutcome(unavailableImportResult(value?.name, importType, props?.propertyID, e?.message));
            return;
          }
          notify(`Operation fail:${e.message}`, {
            type: "error",
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
        },
      }
    );
  };

  const errorMessage = (error: any) => {
    notify(error, {
      type: "error",
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
  };

  const validateFile = (newFile) => {
    if (!newFile) return;
    // if (newFile.size > file_size) {
    //   errorMessage("File size exceeds 4Mb");
    //   return false;
    // }
    if (!file_type.includes(newFile.type)) {
      errorMessage("Supported file format is Excel and CSV files only.");
      return false;
    }
    return true;
  };

  const handleChange = (event: any) => {
    const newFile = event.target.files[0];
    event.target.value = "";
    if (validateFile(newFile)) {
      onSubmit(newFile);
    }
  };

  return (
    <>
      {isLoading ? (
        <Button component="label" variant="outlined" style={{ ...buttonStyle }}>
          <CircularProgress size="20px" />
          <h5
            style={{
              fontSize: "12px",
              color: "#303030",
              fontFamily: "OpenSans-Regular",
            }}
          >
            &nbsp; Please wait...
          </h5>
        </Button>
      ) : (
        <>
          <Button
            component="label"
            startIcon={startIcon == true ? <MdOutlineUploadFile size={18}/> :false }
            variant="outlined"
            className="px-3"
            style={{ ...buttonStyle }}
          >
            {buttonText}
            <VisuallyHiddenInput
              ref={fileInputRef}
              type="file"
              onChange={handleChange}
              accept={file_type.join(", ")}
            />
          </Button>
        </>
      )}
      {showsImportResult && (
        <ImportResultDialog
          open={Boolean(importResult)}
          result={importResult}
          onClose={closeImportResult}
        />
      )}
    </>
  );
};

export function shouldRefreshImportList(result) {
  if (result?.overallResult === "Completed") return true;
  if (result?.overallResult !== "Failed") return false;
  const counts = result.counts || {};
  const entities = result.entities || {};
  return (Number(counts.added) || 0) + (Number(counts.updated) || 0) > 0
    || (Number(entities.floorPlansCreated) || 0) + (Number(entities.unitsCreated) || 0) > 0;
}

function unavailableImportResult(fileName, importType, propertyId, serverMessage) {
  const unavailable = !serverMessage || serverMessage === "Internal Server Error";
  return {
    fileName: fileName || "",
    propertyName: "",
    propertyId: propertyId != null ? String(propertyId) : "",
    importType,
    overallResult: "Failed",
    message: unavailable
      ? "The import failed before a confirmed result was available. No changes from this upload are confirmed."
      : serverMessage,
    deletionNote: "No records deleted. Records omitted from the file were left unchanged.",
    totalDataRows: 0,
    counts: { added: 0, updated: 0, unchanged: 0, rejected: 0, notProcessed: 0 },
    entities: { floorPlansCreated: 0, unitsCreated: 0 },
    rows: [],
  };
}

export default InputFileImport;
